import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import Logo from "./Logo";
import {
  LayoutDashboard,
  Users,
  ReceiptText,
  Wallet,
  LifeBuoy,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  Store,
  ShieldCheck,
  CalendarDays,
  UserCog,
  User as UserIcon,
  MessageCircle,
  CreditCard,
  Ban,
  Gift,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { useUsage } from "../lib/useUsage";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Button } from "./ui/button";
import WhatsAppFloat from "./WhatsAppFloat";
import ShopNotificationBell from "./ShopNotificationBell";
import TrialCountdownStrip from "./TrialCountdownStrip";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";

const baseNav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/guarantors", label: "Guarantors", icon: ShieldCheck, testid: "nav-guarantors" },
  { to: "/loans", label: "Loans", icon: ReceiptText, testid: "nav-loans" },
  { to: "/payments", label: "Payments", icon: Wallet, testid: "nav-payments" },
  { to: "/recovery", label: "Recovery", icon: LifeBuoy, testid: "nav-recovery" },
  { to: "/recovery/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-recovery-calendar" },
  { to: "/whatsapp", label: "WhatsApp", icon: MessageCircle, testid: "nav-whatsapp" },
  { to: "/risk", label: "Risk Check", icon: ShieldCheck, testid: "nav-risk" },
  { to: "/blacklist", label: "Blacklist", icon: Ban, testid: "nav-blacklist" },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/reports/salesmen", label: "Salesmen", icon: UserCog, testid: "nav-salesmen" },
  { to: "/refer", label: "Refer & Earn", icon: Gift, testid: "nav-refer" },
];
const ownerOnlyNav = [
  { to: "/users", label: "Users", icon: UserCog, testid: "nav-users" },
  { to: "/subscription", label: "Subscription", icon: CreditCard, testid: "nav-subscription" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

function SidebarInner({ onNavigate, isOwner }) {
  return (
    <div className="flex h-full flex-col bg-slate-900 text-slate-100">
      <div className="flex items-center gap-2 px-5 py-6 border-b border-slate-800 shrink-0">
        <Logo size={36} priority />
        <div>
          <div className="font-display font-bold text-lg tracking-tight">HisaabPro</div>
          <div className="text-xs text-slate-400 -mt-0.5">Hisaab strong, recovery strong</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1 min-h-0">
        {baseNav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            data-testid={n.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                isActive
                  ? "bg-emerald-600/20 text-emerald-300 border border-emerald-600/30"
                  : "text-slate-300 hover:bg-slate-800 hover:text-white border border-transparent"
              }`
            }
          >
            <n.icon className="w-[18px] h-[18px]" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      {/* Account section — always pinned at bottom so Users/Subscription/Settings never get cut off */}
      {isOwner && (
        <div className="shrink-0 border-t border-slate-800 px-3 py-3 space-y-1" data-testid="sidebar-account-section">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-3 pb-1">Account</div>
          {ownerOnlyNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              onClick={onNavigate}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-emerald-600/20 text-emerald-300 border border-emerald-600/30"
                    : "text-slate-300 hover:bg-slate-800 hover:text-white border border-transparent"
                }`
              }
            >
              <n.icon className="w-[18px] h-[18px]" />
              {n.label}
            </NavLink>
          ))}
        </div>
      )}
      <div className="px-5 py-3 text-[10px] text-slate-500 border-t border-slate-800 shrink-0">
        HisaabPro · Pakistan ke shop owners ke liye
      </div>
    </div>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const usage = useUsage();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const isOwner = user?.role === "owner";

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-[250px] shrink-0 fixed inset-y-0 left-0 h-screen overflow-hidden">
        <SidebarInner isOwner={isOwner} />
      </aside>

      <div className="flex-1 lg:ml-[250px] min-w-0">
        {/* Trial / Subscription countdown strip (top-of-app, last 7 days + overdue) */}
        <TrialCountdownStrip usage={usage} />

        {/* Header */}
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200 h-14 sm:h-16 flex items-center justify-between px-3 sm:px-6">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden h-10 w-10 shrink-0"
                  data-testid="open-sidebar-btn"
                  aria-label="Open menu"
                >
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-[260px]">
                <SidebarInner onNavigate={() => setMobileOpen(false)} isOwner={isOwner} />
              </SheetContent>
            </Sheet>
            <div className="min-w-0">
              <div className="text-[10px] sm:text-xs text-slate-500 font-medium leading-none">Shop</div>
              <div className="text-sm font-display font-semibold truncate">
                {user?.shop_name}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <ShopNotificationBell />
            {isOwner ? (
              <span
                data-testid="header-role-owner"
                className="hidden sm:inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200"
              >
                <ShieldCheck className="w-3 h-3" /> Owner
              </span>
            ) : (
              <span
                data-testid="header-role-staff"
                className="hidden sm:inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold bg-sky-50 text-sky-700 border border-sky-200"
              >
                <UserIcon className="w-3 h-3" /> Staff
              </span>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-btn"
                  className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-slate-100 transition"
                >
                  <div className="w-9 h-9 rounded-full bg-emerald-600 text-white flex items-center justify-center font-semibold">
                    {(user?.owner_name || user?.email || "U").charAt(0).toUpperCase()}
                  </div>
                  <div className="hidden sm:block text-left">
                    <div className="text-sm font-semibold leading-tight">{user?.owner_name}</div>
                    <div className="text-xs text-slate-500">{user?.email}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="p-3 sm:p-6 lg:p-8 max-w-[1400px] mx-auto pb-24 sm:pb-8">{children}</main>
      </div>
      <WhatsAppFloat label="WhatsApp Support" />
    </div>
  );
}
